import * as React from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { ProgramsIcon } from 'src/assets/icons/customIcons/page-icons/Programs'
import { useAdminAccess, useApi, type APIResponse } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import { ErrorBoundary } from 'src/views/components/Error'
import ErrorElement from 'src/views/components/ErrorElement'
import Loader from 'src/views/components/Loader'
import SemesterSelect from 'src/views/includes/SemesterSelect'
import SubPageLayout, { type Page } from 'src/views/includes/SubPageLayout'

export default function AdministratorCourseLayout(): JSX.Element | null {
  return (
    <React.Suspense fallback={<Loader className="m-auto flex" />}>
      <ErrorBoundary errorElement={<ErrorElement />}>
        <PageContent />
      </ErrorBoundary>
    </React.Suspense>
  )
}

function PageContent(): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const { courseId } = useParams()
  const t = useTranslatable()
  const { can } = useAdminAccess()
  const locale = useLocale()
  const { pathname } = useLocation()
  const activeSemester = useActiveSemester()
  const [searchParams, setSearchParams] = useSearchParams()
  const semIdFromUrl = searchParams.get('semId')
  const semId = (semIdFromUrl != null && semIdFromUrl.trim() !== '' ? semIdFromUrl : null) ?? activeSemester?.id ?? ''

  React.useEffect(() => {
    if (activeSemester?.id == null) return
    const raw = searchParams.get('semId')
    if (raw != null && raw.trim() !== '') return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('semId', String(activeSemester.id))
        return next
      },
      { replace: true }
    )
  }, [activeSemester?.id, searchParams, setSearchParams])
  const coursesListPath = React.useMemo(() => {
    const base = `/${locale}/administrator/administration/courses`
    const q = semId != null && String(semId).length > 0 ? String(semId) : ''
    return q.length > 0 ? `${base}?semId=${encodeURIComponent(q)}` : base
  }, [locale, semId])

  const parsedSemId = Number.parseInt(String(semId), 10)
  const hasValidSemId = String(semId).trim() !== '' && Number.isFinite(parsedSemId)

  const { data }: APIResponse<Api.AdministratorCourse | null, Error> = useApi({
    endpoint: Api.getAdministratorCourse,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          semId: parsedSemId,
        },
        args: {
          id: courseId!,
        },
      }),
      [courseId, headers, parsedSemId]
    ),
    shouldFetch: courseId != null && hasValidSemId,
    suspense: false,
  })

  const subBreadcrumbsItems = [
    {
      page: t('course:learning_courses'),
      path: coursesListPath,
    },
    { page: data?.name ?? `#${courseId ?? ''}`, path: pathname },
  ]

  const pages = React.useMemo<Page[]>(
    () => [
      { title: t('common:edit'), link: `edit?semId=${encodeURIComponent(semId)}` },
      { title: t('course:prerequisites'), link: `prerequisites?semId=${encodeURIComponent(semId)}` },
      { title: t('subPage:groups'), link: `groups?semId=${encodeURIComponent(semId)}` },
      ...(can('courseScores')
        ? [{ title: t('subPage:scores'), link: `scores?semId=${encodeURIComponent(semId)}` }]
        : []),
      { title: t('student:students'), link: `students?semId=${encodeURIComponent(semId)}` },
      ...(can('courseScoreStatistics')
        ? [
            {
              title: t('subPage:score_statistics'),
              link: `score-statistics?semId=${encodeURIComponent(semId)}`,
              end: false,
            },
          ]
        : []),
      ...(can('courseSurveyResults')
        ? [
            {
              title: t('subPage:survey_results'),
              link: `survey-results?semId=${encodeURIComponent(semId)}`,
              end: false,
            },
          ]
        : []),
    ],
    [t, semId, can]
  )

  return (
    <SubPageLayout
      Icon={ProgramsIcon}
      title={data?.name ?? '...'}
      subBreadcrubms={subBreadcrumbsItems}
      pages={pages}
      filter={<SemesterSelect />}
    />
  )
}
